<script lang="ts" setup>
  import { usePDF } from '@/store/pdfStore'
  import { useSignatures } from '@/store/signatureStore'
  import '@tato30/vue-pdf/style.css'
  import SignaturePad from 'signature_pad'
  import { onMounted, reactive, ref, onBeforeUnmount } from 'vue'
  import Vue3Signature from 'vue3-signature'
  import Button from '../ui/button/Button.vue'
  import { useThemeColor } from '@/composables/useThemeColor'

  const SIGNATURE_FORMAT = 'image/svg+xml'
  const EMPTY_SIGNATURE =
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB2aWV3Qm94PSIwIDAgNTAwIDI1MCIgd2lkdGg9IjUwMCIgaGVpZ2h0PSIyNTAiPjwvc3ZnPg=='

  const props = defineProps({
    left: {
      type: String,
      required: false,
      default: '50%'
    },
    top: {
      type: String,
      required: false,
      default: '50%'
    },
    width: {
      type: String,
      required: false,
      default: '500px'
    },
    height: {
      type: String,
      required: false,
      default: '250px'
    },
    zIndex: {
      type: String,
      required: false,
      default: '1000'
    },
    data: {
      type: String,
      required: false,
      default: ''
    }
  })

  const signatureStore = useSignatures()
  const { selectedForm } = usePDF()
  const themeColor = useThemeColor()

  onBeforeUnmount(() => {
    signatureStore.cleanup()
  })

  const state = reactive({
    sig: SignaturePad,
    count: 0,
    option: {
      penColor: 'rgb(0, 0, 0)',
      backgroundColor: 'rgb(255,255,255)'
    },
    disabled: false
  })

  const signature1 = ref<{
    undo: () => void
    clear: () => void
    save: (t: any) => any
    isEmpty: () => boolean
    fromDataURL: (arg0: string) => void
    addWaterMark: (arg0: {
      text: string
      font: string
      style: string
      fillStyle: string
      strokeStyle: string
      x: number
      y: number
      sx: number
      sy: number
    }) => void
    disabled: boolean
    defaultUrl: string
    clearOnResize: boolean
    w: number
    h: number
    sigOption: {
      minWidth: number
      maxWidth: number
      penColor: string
      backgroundColor: string
      throttle: number
      minDistance: number
    }
    waterMark: {
      text: string
      font: string
      style: string
      fillStyle: string
      strokeStyle: string
      x: number
      y: number
      sx: number
      sy: number
    }
  } | null>(null)

  // TODO signature1.value?.isEmpty() does report false when opening the signature
  // pad saving it without any modifications return signature1.value?.isEmpty()
  function isEmpty(): boolean {
    return signature1.value?.save(SIGNATURE_FORMAT) == EMPTY_SIGNATURE
  }

  //TODO: This function is not working as expected
  // Avoid using it for now
  function isEmptyPad(): boolean {
    const currentSignature = signature1.value?.save(SIGNATURE_FORMAT)

    // Extract content within <svg> tags
    const extractContent = (svg: string) => {
      const match = svg.match(/<svg[^>]*>([\s\S]*)<\/svg>/)
      return match ? match[1].trim() : ''
    }

    const currentContent = extractContent(currentSignature)
    const emptyContent = extractContent(EMPTY_SIGNATURE)

    const val = currentContent === emptyContent
    return val
  }

  function update(sel: any) {
    if (!sel) {
      console.warn('No selected signature annotation')
      return
    }

    const signatureKey = selectedForm?.documentProperties.id + sel.name
    signatureStore.signatures[signatureKey] = {
      data_uri: signature1.value?.save(SIGNATURE_FORMAT),
      annotation_id: sel.id,
      annotation_field_name: sel.name
    }
  }

  function save() {
    // if (isEmptyPad()) {
    //   alert('Bitte unterschreiben Sie das Dokument vor dem Absenden.')
    //   return
    // }

    if (!isEmpty()) {
      update(signatureStore.selectedSignatureFieldObject)
    }

    signatureStore.showSignModal = false
  }

  function clear() {
    signature1.value?.clear()
    const sel = signatureStore.selectedSignatureFieldObject
    if (!sel) return
    if (!Object.prototype.hasOwnProperty.call(signatureStore.signatures, sel.name)) return
    delete signatureStore.signatures[sel.name]
    signatureStore.showSignModal = false
    // update(sig.selectedSignatureFieldObject)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function undo() {
    signature1.value?.undo()
  }

  onMounted(() => {
    signature1.value?.fromDataURL(props.data)
  })
</script>

<template>
  <div class="fixed left-0 top-0 z-50 h-screen w-screen bg-black bg-opacity-50">
    <div
      class="sign-here absolute z-10 -translate-x-1/2 -translate-y-1/2"
      :style="{ left: left, top: top, width: width, height: height, zIndex: zIndex }"
    >
      <Vue3Signature
        ref="signature1"
        :disabled="state.disabled"
        :option="state.option"
      />
      <div class="mt-3 flex flex-wrap items-center justify-between space-x-1">
        <div class="inline-flex items-center justify-center whitespace-nowrap">
          <Button
            class="mt-3 bg-white text-black hover:bg-gray-200"
            @click="signatureStore.showSignModal = false"
          >
            Abbrechen
          </Button>
          <Button
            class="ml-2 mt-3 bg-white text-black hover:bg-gray-200"
            variant="destructive"
            @click="clear"
          >
            Zurücksetzen
          </Button>
        </div>
        <Button
          class="mt-3"
          :class="`bg-${themeColor}-600 hover:bg-${themeColor}-800`"
          @click="save"
        >
          Einfügen
        </Button>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .sign-here {
    box-sizing: content-box;
    letter-spacing: 2px;
    color: #f39;
    font-family: sans-serif;
    font-size: 55px;
    font-weight: bold;
    border: 1px solid rgb(214 211 209);
    max-width: 80%;
    box-sizing: border-box;
  }
</style>
